import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaMoneyBillWave, 
  FaClock, 
  FaUsers, 
  FaSmile,
  FaChartBar,
  FaHandshake
} from 'react-icons/fa';

const ValueSection = styled.section`
  padding: 5rem 2rem;
  background: #ffffff;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 120%;
    height: 120%;
    background-image: 
      linear-gradient(45deg, transparent 48%, rgba(8, 145, 178, 0.02) 49%, rgba(8, 145, 178, 0.02) 51%, transparent 52%),
      linear-gradient(-45deg, transparent 48%, rgba(8, 145, 178, 0.02) 49%, rgba(8, 145, 178, 0.02) 51%, transparent 52%);
    background-size: 30px 30px;
    pointer-events: none;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #1a1a1a 0%, #0891b2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: #6b7280;
  max-width: 700px;
  margin: 0 auto;
  font-weight: 500;
`;

const ValueGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 3rem;
  margin-bottom: 5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    margin-bottom: 3rem;
  }
`;

const ValueCard = styled(motion.div)`
  text-align: center;
  position: relative;
`;

const IconWrapper = styled(motion.div)`
  width: 100px;
  height: 100px;
  margin: 0 auto 2rem;
  position: relative;
  
  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
    margin-bottom: 1.5rem;
  }
  
  &::before {
    content: '';
    position: absolute;
    inset: -20px;
    background: radial-gradient(circle, rgba(8, 145, 178, 0.1) 0%, transparent 70%);
    border-radius: 50%;
    filter: blur(20px);
  }
`;

const IconCircle = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: white;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ValueTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ValueDescription = styled.p`
  color: #6b7280;
  line-height: 1.6;
  font-size: 1.1rem;
  font-weight: 400;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ROISection = styled(motion.div)`
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 2px solid #0891b2;
  border-radius: 30px;
  padding: 4rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(8, 145, 178, 0.1);
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    border-radius: 20px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(8, 145, 178, 0.08) 0%, transparent 70%);
    animation: rotate 20s linear infinite;
  }
  
  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ROITitle = styled.h3`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: #1a1a1a;
  position: relative;
  z-index: 1;
  font-weight: 800;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 1.5rem;
  }
`;

const ROIGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
`;

const ROIMetric = styled.div`
  background: #ffffff;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 2px solid #f0f0f0;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const MetricValue = styled.div`
  font-size: 3rem;
  font-weight: 800;
  color: #0891b2;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const MetricLabel = styled.div`
  color: #6b7280;
  font-weight: 500;
`;

const ROIDescription = styled.p`
  color: #6b7280;
  font-size: 1.1rem;
  position: relative;
  z-index: 1;
  font-weight: 500;
`;

const values = [
  {
    icon: <FaMoneyBillWave />,
    title: "Reduce Support Costs",
    description: "Cut customer support expenses by up to 70% while maintaining service quality"
  },
  {
    icon: <FaClock />,
    title: "24/7 Availability",
    description: "Never miss a player query with round-the-clock intelligent support"
  },
  {
    icon: <FaUsers />,
    title: "Scale Effortlessly",
    description: "Handle thousands of conversations simultaneously without adding staff"
  },
  {
    icon: <FaSmile />,
    title: "Boost Satisfaction",
    description: "Increase player satisfaction scores with instant, accurate responses"
  },
  {
    icon: <FaChartBar />,
    title: "Data-Driven Insights",
    description: "Understand player needs and optimize your casino operations"
  },
  {
    icon: <FaHandshake />,
    title: "Increase Retention",
    description: "Keep players engaged and reduce churn with personalized support"
  }
];

const ValueProposition = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const metrics = [
    { value: "3x", label: "ROI in First Year" },
    { value: "75%", label: "First Contact Resolution" },
    { value: "<15%", label: "Deflected to Human Agents" },
    { value: "92%", label: "Player Satisfaction" }
  ];

  return (
    <ValueSection id="value">
      <Container ref={ref}>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            Transform Your Casino's Bottom Line
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Faroflow delivers measurable ROI from day one, improving both player experience and operational efficiency
          </SectionSubtitle>
        </SectionHeader>

        <ValueGrid>
          {values.map((value, index) => (
            <ValueCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <IconWrapper
                animate={inView ? {
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0]
                } : {}}
                transition={{
                  duration: 4,
                  delay: index * 0.2,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              >
                <IconCircle>{value.icon}</IconCircle>
              </IconWrapper>
              <ValueTitle>{value.title}</ValueTitle>
              <ValueDescription>{value.description}</ValueDescription>
            </ValueCard>
          ))}
        </ValueGrid>

        <ROISection
          initial={{ opacity: 0, scale: 0.9 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <ROITitle>See the Impact in Numbers</ROITitle>
          <ROIGrid>
            {metrics.map((metric, index) => (
              <ROIMetric key={index}>
                <MetricValue>{metric.value}</MetricValue>
                <MetricLabel>{metric.label}</MetricLabel>
              </ROIMetric>
            ))}
          </ROIGrid>
          <ROIDescription>
            Based on data from leading online casinos using Faroflow, 
            operators see positive ROI within 3 months and full cost recovery within 6 months.
          </ROIDescription>
        </ROISection>
      </Container>
    </ValueSection>
  );
};

export default ValueProposition; 